<template>
  <div class="record-detail-page app-page app-page--fill">
    <PageHeader size="page" divider>
      <template #title>
        <el-button type="primary" :icon="ArrowLeft" @click="goBack">返回</el-button>
        <span>{{ pageTitle }}</span>
      </template>
    </PageHeader>

    <div v-loading="loading" class="detail-body" element-loading-text="加载中...">
      <el-empty v-if="!recordDetail && !loading" description="暂无体检记录详情" />

      <template v-else-if="recordDetail">
        <el-card class="summary-card" shadow="hover">
          <template #header>
            <PageHeader size="card">
              <template #title>基本信息</template>
            </PageHeader>
          </template>
          <div class="summary-grid">
            <div class="summary-item">
              <span class="summary-label">评估类型</span>
              <span class="summary-value">{{ recordDetail.评估类型 || '-' }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">体检人员</span>
              <span class="summary-value">{{ recordDetail.体检人员 || '-' }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">提交时间</span>
              <span class="summary-value">{{ formatDateText(recordDetail.提交时间) }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">数据唯一ID</span>
              <span class="summary-value">{{ recordDetail.数据唯一ID || '-' }}</span>
            </div>
            <div v-if="recordDetail.项目名称 || recordDetail.项目编码" class="summary-item">
              <span class="summary-label">项目名称</span>
              <span class="summary-value">{{ recordDetail.项目名称 || '-' }}</span>
            </div>
            <div v-if="recordDetail.项目名称 || recordDetail.项目编码" class="summary-item">
              <span class="summary-label">项目编码</span>
              <span class="summary-value">{{ recordDetail.项目编码 || '-' }}</span>
            </div>
          </div>
        </el-card>

        <el-card class="fields-card" shadow="hover">
          <template #header>
            <PageHeader size="card">
              <template #title>采集数据</template>
            </PageHeader>
          </template>

          <div v-if="groupedFields.length" class="field-tabs-shell">
            <div ref="fieldTabNavRef" class="field-tab-nav" role="tablist" @scroll="updateFieldTabIndicator">
              <div class="field-tab-indicator" :style="fieldTabIndicatorStyle" />
              <button
                v-for="(group, groupIndex) in groupedFields"
                :key="group.category"
                :ref="(el) => setFieldTabButtonRef(el, groupIndex)"
                class="field-tab-button"
                :class="{ 'field-tab-button--active': group.category === activeFieldCategory }"
                type="button"
                role="tab"
                :aria-selected="group.category === activeFieldCategory"
                @click="setActiveFieldCategory(group.category)"
              >
                {{ group.category }}
              </button>
            </div>

            <div class="field-table-viewport">
              <el-table v-if="activeFieldGroup" :data="activeFieldGroup.items" border stripe size="small" class="field-table">
                    <el-table-column prop="字段名称" label="字段名称" min-width="180" show-overflow-tooltip />
                    <el-table-column prop="数据类型" label="数据类型" width="110" />
                    <el-table-column label="采集值" min-width="260" show-overflow-tooltip>
                      <template #default="{ row }">
                        <div v-if="isOptionType(row)" class="tag-list">
                          <el-tag
                            v-for="item in getSelectedOptions(row)"
                            :key="item"
                            type="success"
                            size="small"
                          >
                            {{ item }}
                          </el-tag>
                          <span v-if="!getSelectedOptions(row).length">-</span>
                        </div>
                        <span v-else>{{ row.数据值 || '-' }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column prop="预显备注" label="预显备注" min-width="180" show-overflow-tooltip>
                      <template #default="{ row }">{{ row.预显备注 || '-' }}</template>
                    </el-table-column>
                    <el-table-column label="备注图片" min-width="180" align="center">
                      <template #default="{ row }">
                        <div v-if="getRemarkImages(row).length" class="remark-image-list">
                          <div
                            v-for="(image, imageIndex) in getRemarkImages(row)"
                            :key="`${getRemarkImageName(image)}-${imageIndex}`"
                            class="remark-image-item"
                            :title="getRemarkImageName(image)"
                          >
                            <el-image
                              v-if="getRemarkImageUrl(image)"
                              class="remark-thumb"
                              :src="getRemarkImageUrl(image)"
                              :preview-src-list="getRemarkPreviewList(row)"
                              :initial-index="getRemarkImagePreviewIndex(row, image)"
                              fit="cover"
                              preview-teleported
                              hide-on-click-modal
                            />
                            <div v-else class="remark-thumb remark-thumb--placeholder">
                              {{ getRemarkImagePlaceholderText(image) }}
                            </div>
                          </div>
                        </div>
                        <span v-else>-</span>
                      </template>
                    </el-table-column>
                  </el-table>
            </div>
          </div>
          <el-empty v-else description="暂无采集数据" />
        </el-card>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ArrowLeft } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import PageHeader from '@/components/PageHeader.vue';
import { ParameterSettings, list } from '@/network/index';
import { useRouteTitle } from '@/utils/useRouteTitle';

const route = useRoute();
const router = useRouter();
const pageTitle = useRouteTitle('体检记录详情');

const loading = ref(false);
const recordDetail = ref<any | null>(null);
const remarkImageCache = ref<Record<string, { url: string; loading: boolean; error: boolean }>>({});
const activeFieldCategory = ref('');
const fieldTabNavRef = ref<HTMLElement | null>(null);
const fieldTabButtonRefs = ref<HTMLElement[]>([]);
const fieldTabIndicator = ref({ left: 0, width: 0 });

const formatDateText = (value: any): string => {
  if (!value) return '-';
  return String(value).replace('T', ' ');
};

const getRecordFields = () => {
  const fields = recordDetail.value?.房屋建筑定期体检数据采集原始子表集合;
  return Array.isArray(fields) ? fields : [];
};

const groupedFields = computed(() => {
  const groupMap = new Map<string, any[]>();
  getRecordFields()
    .slice()
    .sort((a: any, b: any) => Number(a?.index ?? 0) - Number(b?.index ?? 0))
    .forEach((item: any) => {
      const category = item?.所属类别 || '未分类';
      if (!groupMap.has(category)) groupMap.set(category, []);
      groupMap.get(category)?.push(item);
    });

  return Array.from(groupMap.entries()).map(([category, items]) => ({
    category,
    items
  }));
});

const activeFieldGroup = computed(() => {
  return groupedFields.value.find((group) => group.category === activeFieldCategory.value) || groupedFields.value[0];
});

watch(groupedFields, (groups) => {
  fieldTabButtonRefs.value = [];
  if (!groups.length) {
    activeFieldCategory.value = '';
    return;
  }
  if (!groups.some((group) => group.category === activeFieldCategory.value)) {
    activeFieldCategory.value = groups[0].category;
  }
  updateFieldTabIndicator();
});

const getFieldGroupIndex = (category: string) => {
  return groupedFields.value.findIndex((group) => group.category === category);
};

const fieldTabIndicatorStyle = computed(() => {
  return {
    width: `${fieldTabIndicator.value.width}px`,
    transform: `translateX(${fieldTabIndicator.value.left}px)`
  };
});

const setFieldTabButtonRef = (el: Element | null, index: number) => {
  if (el instanceof HTMLElement) {
    fieldTabButtonRefs.value[index] = el;
  }
};

const updateFieldTabIndicator = async () => {
  await nextTick();
  const activeIndex = getFieldGroupIndex(activeFieldCategory.value);
  const activeButton = fieldTabButtonRefs.value[activeIndex];
  const nav = fieldTabNavRef.value;
  if (!activeButton || !nav) {
    fieldTabIndicator.value = { left: 0, width: 0 };
    return;
  }

  fieldTabIndicator.value = {
    left: activeButton.offsetLeft - nav.scrollLeft,
    width: activeButton.offsetWidth
  };
};

const setActiveFieldCategory = (category: string) => {
  activeFieldCategory.value = category;
};

const handleWindowResize = () => {
  updateFieldTabIndicator();
};

const isOptionType = (row: any) => row?.数据类型 === '选项' || row?.数据类型 === '多选';

const getSelectedOptions = (row: any): string[] => {
  const selectedRequired = Array.isArray(row?.已选必选项) ? row.已选必选项 : [];
  const selectedOptional = Array.isArray(row?.已选可选项) ? row.已选可选项 : [];
  const merged = [...selectedRequired, ...selectedOptional].filter(Boolean);
  if (merged.length) return merged.map(String);
  return row?.数据值 ? String(row.数据值).split(',').map(item => item.trim()).filter(Boolean) : [];
};

const getRemarkImages = (row: any): any[] => {
  if (Array.isArray(row?.备注图像列表)) return row.备注图像列表;
  if (typeof row?.备注图像列表json === 'string') {
    try {
      const parsed = JSON.parse(row.备注图像列表json);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const getRemarkImageName = (image: any): string => {
  if (typeof image === 'string') return image.trim();
  if (!image || typeof image !== 'object') return '';
  return String(
    image.名称 ??
      image.fileName ??
      image.文件名 ??
      image.name ??
      image.图片名称 ??
      ''
  ).trim();
};

const parseDownloadResponseToUrl = (res: any): string => {
  if (res instanceof Blob) {
    return URL.createObjectURL(res);
  }

  const data = res?.response ?? res?.data ?? res;
  if (data instanceof Blob) {
    return URL.createObjectURL(data);
  }

  if (typeof data === 'string') {
    if (data.startsWith('http') || data.startsWith('blob:') || data.startsWith('data:')) {
      return data;
    }
    return `data:image/jpeg;base64,${data}`;
  }

  return '';
};

const setRemarkImageState = (fileName: string, state: Partial<{ url: string; loading: boolean; error: boolean }>) => {
  remarkImageCache.value = {
    ...remarkImageCache.value,
    [fileName]: {
      ...(remarkImageCache.value[fileName] || { url: '', loading: false, error: false }),
      ...state
    }
  };
};

const clearRemarkImageCache = () => {
  Object.values(remarkImageCache.value).forEach((item) => {
    if (item.url?.startsWith('blob:')) {
      URL.revokeObjectURL(item.url);
    }
  });
  remarkImageCache.value = {};
};

const getRemarkImageUrl = (image: any): string => {
  const fileName = getRemarkImageName(image);
  return fileName ? remarkImageCache.value[fileName]?.url || '' : '';
};

const getRemarkImagePlaceholderText = (image: any): string => {
  const fileName = getRemarkImageName(image);
  if (!fileName) return '无名称';
  return remarkImageCache.value[fileName]?.loading ? '加载中' : '加载失败';
};

const getRemarkPreviewList = (row: any): string[] => {
  return getRemarkImages(row)
    .map((image) => getRemarkImageUrl(image))
    .filter(Boolean);
};

const getRemarkImagePreviewIndex = (row: any, image: any): number => {
  const currentUrl = getRemarkImageUrl(image);
  const index = getRemarkPreviewList(row).findIndex((url) => url === currentUrl);
  return index > -1 ? index : 0;
};

const loadRemarkImage = async (fileName: string) => {
  if (!fileName) return;
  const cached = remarkImageCache.value[fileName];
  if (cached?.url || cached?.loading) return;

  setRemarkImageState(fileName, { loading: true, error: false });
  try {
    const res = await list.downloadPhoto({ 文件名: fileName });
    const url = parseDownloadResponseToUrl(res);
    if (!url) throw new Error('图片内容为空');
    setRemarkImageState(fileName, { url, loading: false, error: false });
  } catch (error) {
    console.warn(`加载备注图片失败: ${fileName}`, error);
    setRemarkImageState(fileName, { url: '', loading: false, error: true });
  }
};

const loadAllRemarkImages = () => {
  const fileNames = Array.from(new Set(
    getRecordFields()
      .flatMap((row: any) => getRemarkImages(row).map((image) => getRemarkImageName(image)))
      .filter(Boolean)
  ));
  fileNames.forEach((fileName) => {
    loadRemarkImage(fileName);
  });
};

const refreshActiveTableLayout = async () => {
  await nextTick();
  window.dispatchEvent(new Event('resize'));
};

const loadRecordDetail = async () => {
  const id = route.query.id;
  if (!id) {
    ElMessage.warning('缺少体检记录ID');
    return;
  }

  try {
    loading.value = true;
    clearRemarkImageCache();
    const res = await ParameterSettings.houseCheckPlanRecordDetailById({ id });
    if (res && res.status === 200 && res.success && res.response) {
      recordDetail.value = res.response;
      loadAllRemarkImages();
    } else {
      recordDetail.value = null;
      ElMessage.error(res?.msg || '加载体检记录详情失败');
    }
  } catch (error) {
    console.error('加载体检记录详情失败:', error);
    recordDetail.value = null;
    ElMessage.error('加载体检记录详情失败');
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  router.back();
};

onMounted(() => {
  loadRecordDetail();
  window.addEventListener('resize', handleWindowResize);
});

watch(activeFieldCategory, () => {
  refreshActiveTableLayout();
  updateFieldTabIndicator();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleWindowResize);
  clearRemarkImageCache();
});
</script>

<style lang="less" scoped>
.record-detail-page {
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .detail-body {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(160px, 1fr));
    gap: 12px;
  }

  .summary-item {
    padding: 12px;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    background: #f8fafc;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .summary-label {
    color: #64748b;
    font-size: 13px;
  }

  .summary-value {
    color: #1f2937;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .fields-card {
    flex: 1;
    min-height: 0;
  }

  .field-tabs-shell {
    min-height: 420px;
    border: 1px solid #dcdfe6;
    background: #fff;
    overflow: hidden;
  }

  .field-tab-nav {
    position: relative;
    display: flex;
    align-items: stretch;
    min-height: 40px;
    background: #f5f7fa;
    border-bottom: 1px solid #dcdfe6;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .field-tab-indicator {
    position: absolute;
    left: 0;
    top: 0;
    bottom: -1px;
    border: 1px solid #dcdfe6;
    border-bottom-color: #fff;
    background: #fff;
    transition: transform 260ms ease, width 260ms ease;
    z-index: 0;
  }

  .field-tab-button {
    position: relative;
    z-index: 1;
    min-width: 112px;
    height: 40px;
    padding: 0 16px;
    border: 0;
    background: transparent;
    color: #606266;
    font-size: 14px;
    line-height: 40px;
    cursor: pointer;
    transition: color 180ms ease;
    white-space: nowrap;
  }

  .field-tab-button--active {
    color: #409eff;
    font-weight: 500;
  }

  .field-table-viewport {
    min-height: 320px;
    padding: 12px;
    overflow: hidden;
    background: #fff;
  }

  .field-table {
    min-height: 420px;
  }

  .tag-list {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .remark-image-list {
    display: flex;
    justify-content: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .remark-image-item {
    width: 44px;
    height: 44px;
  }

  .remark-thumb {
    width: 44px;
    height: 44px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #f5f7fa;
    overflow: hidden;
    cursor: pointer;
  }

  .remark-thumb--placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 12px;
    line-height: 16px;
    cursor: default;
    text-align: center;
    padding: 2px;
  }

  @media (max-width: 1200px) {
    .summary-grid {
      grid-template-columns: repeat(2, minmax(160px, 1fr));
    }
  }

  @media (max-width: 768px) {
    .summary-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
